import { useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PageContainer } from '@/components/common/PageContainer'
import { PageEmpty, PageError, PageLoading } from '@/components/common/PageStates'
import { AppButton } from '@/components/rfid/AppButton'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { TransferContextCard } from '@/components/rfid/TransferContextCard'
import { getMovement, type MovementId } from '@/config/movements'
import { rfidService, type RfidDocumentRow } from '@/services/rfid.service'
import { useDocumentWizardStore } from '@/stores/documentWizardStore'
import { getDocumentItems } from '@/utils/document'
import { transferItemsHaveKits } from '@/utils/transferDocument'
import { movementFlowLabel } from '@/utils/movementFlow'
import { BASE_DOC_STORAGE_KEY } from './TransferDocListPage'

function readBaseDocument(): RfidDocumentRow | null {
  try {
    const raw = sessionStorage.getItem(BASE_DOC_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as RfidDocumentRow
  } catch {
    return null
  }
}

function itemCode(item: Record<string, unknown>): string {
  return String(
    item.Material ?? item.Component ?? item.ITEMCODE ?? item.Material_Code ?? '—'
  )
}

function itemDesc(item: Record<string, unknown>): string {
  return String(
    item.MaterialDesc ?? item.Description ?? item.ComponentDesc ?? item.MATERIAL_DESC ?? ''
  )
}

function itemQty(item: Record<string, unknown>): string {
  return String(item.Quantity ?? item.DISP_REC_QTY ?? item.KitQty ?? '—')
}

export default function TransferItemPickPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const movementParam = params.get('movement')
  const bpCode = params.get('bpCode') ?? ''
  const customerName = params.get('customerName') ?? ''
  const toBpCode = params.get('toBpCode') ?? ''
  const toCustomerName = params.get('toCustomerName') ?? ''
  const wantsBaseDoc = params.get('baseDoc') === '1'

  const initTransferOut = useDocumentWizardStore((s) => s.initTransferOut)
  const initTransferIn = useDocumentWizardStore((s) => s.initTransferIn)
  const loadCustomerPostingContext = useDocumentWizardStore((s) => s.loadCustomerPostingContext)

  const [starting, setStarting] = useState(false)
  const [baseDocument] = useState<RfidDocumentRow | null>(() =>
    wantsBaseDoc ? readBaseDocument() : null
  )

  const validMovement =
    movementParam === 'transfer_out' || movementParam === 'transfer_in'
  const movement = validMovement ? getMovement(movementParam as MovementId) : null
  const isTransferIn = movement?.id === 'transfer_in'

  const itemsQ = useQuery({
    queryKey: ['transfer-items', bpCode, toBpCode, Boolean(baseDocument)],
    queryFn: () => rfidService.fetchTransferItems(bpCode, toBpCode),
    enabled: Boolean(!isTransferIn && bpCode && toBpCode),
  })

  const previewLines = useMemo(() => {
    if (isTransferIn && baseDocument) return getDocumentItems(baseDocument)
    return itemsQ.data ?? []
  }, [isTransferIn, baseDocument, itemsQ.data])

  const kitHint = !isTransferIn && transferItemsHaveKits(itemsQ.data ?? [])
  const lineCount = isTransferIn
    ? previewLines.length
    : (itemsQ.data ?? []).reduce((n, item) => {
        const comps = (item.Components as { Component?: unknown })?.Component
        if (comps) {
          const list = Array.isArray(comps) ? comps : [comps]
          return n + list.length
        }
        return n + 1
      }, 0)

  if (!validMovement || !movement || !bpCode) {
    return <Navigate to="/" replace />
  }

  if (isTransferIn && !baseDocument) {
    return (
      <Navigate
        to={`/transfer/docs?movement=${movement.id}&bpCode=${encodeURIComponent(bpCode)}&customerName=${encodeURIComponent(customerName)}`}
        replace
      />
    )
  }

  if (!isTransferIn && !toBpCode) {
    return (
      <Navigate
        to={`/transfer/to-customer?movement=${movement.id}&fromBpCode=${encodeURIComponent(bpCode)}&fromCustomerName=${encodeURIComponent(customerName)}`}
        replace
      />
    )
  }

  const startWizard = async () => {
    setStarting(true)
    try {
      if (isTransferIn && baseDocument) {
        initTransferIn(movement.id, bpCode, customerName, baseDocument)
      } else {
        const items = itemsQ.data ?? []
        if (!items.length || !toBpCode) return
        initTransferOut(
          movement.id,
          bpCode,
          customerName,
          toBpCode,
          toCustomerName || toBpCode,
          items
        )
      }
      await loadCustomerPostingContext(bpCode)
      const detailsError = useDocumentWizardStore.getState().customerDetailsError
      if (detailsError) {
        toast.warning(detailsError)
      }
      sessionStorage.removeItem(BASE_DOC_STORAGE_KEY)
      navigate('/wizard/capture')
    } finally {
      setStarting(false)
    }
  }

  const backHref = isTransferIn
    ? `/transfer/docs?movement=${movement.id}&bpCode=${encodeURIComponent(bpCode)}&customerName=${encodeURIComponent(customerName)}`
    : `/transfer/to-customer?movement=${movement.id}&fromBpCode=${encodeURIComponent(bpCode)}&fromCustomerName=${encodeURIComponent(customerName)}`

  const canContinue = isTransferIn
    ? Boolean(baseDocument)
    : Boolean(itemsQ.data?.length)

  return (
    <PageContainer fluid className="!flex !h-full !min-h-0 !flex-col !px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title={isTransferIn ? 'Confirm transfer in' : 'Pick transfer items'}
          subtitle={`${movementFlowLabel(movement)} · ${customerName}`}
          onBack={() => navigate(backHref)}
        />

        <div className="rfid-wizard-body">
          {!isTransferIn && toCustomerName ? (
            <TransferContextCard
              fromLabel={customerName || bpCode}
              fromCode={bpCode}
              toLabel={toCustomerName}
              toCode={toBpCode}
              className="mb-3"
            />
          ) : null}

          {isTransferIn && baseDocument ? (
            <TransferContextCard
              fromLabel={customerName || bpCode}
              fromCode={bpCode}
              refDoc={String(baseDocument.DocumentNo ?? '—')}
              className="mb-3"
            />
          ) : null}

          {!isTransferIn && itemsQ.isLoading ? (
            <PageLoading tip="Loading transfer items…" />
          ) : !isTransferIn && itemsQ.isError ? (
            <PageError
              message="Failed to load items"
              description={(itemsQ.error as Error)?.message}
            />
          ) : previewLines.length === 0 ? (
            <PageEmpty description="No items to transfer." />
          ) : (
            <>
              <div className="rfid-section-label">
                {kitHint
                  ? 'Kit components'
                  : `${lineCount} line${lineCount === 1 ? '' : 's'}`}
              </div>

              <div className="rfid-doc-list">
              {previewLines.map((item, i) => (
                <div
                  key={String(
                    item.Material ??
                      item.Component ??
                      item.ITEMCODE ??
                      item.Material_Code ??
                      i
                  )}
                  className="rfid-doc-row cursor-default"
                  role="listitem"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-extrabold text-foreground">
                      {itemCode(item)}
                    </span>
                    {itemDesc(item) ? (
                      <span className="mt-0.5 block text-[11px] font-semibold leading-[15px] text-muted-foreground line-clamp-2">
                        {itemDesc(item)}
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-[12px] font-extrabold tabular-nums text-foreground">
                    Qty {itemQty(item)}
                  </span>
                </div>
              ))}
              </div>
            </>
          )}
        </div>

        {canContinue ? (
          <div className="rfid-footer-dock">
            <AppButton
              block
              loading={starting}
              disabled={!canContinue}
              onClick={() => void startWizard()}
            >
              {isTransferIn
                ? 'Start input & scan'
                : `Continue with ${lineCount} line${lineCount === 1 ? '' : 's'}`}
            </AppButton>
          </div>
        ) : null}
      </div>
    </PageContainer>
  )
}
